import React, { useState } from "react";
import {
  Box, Typography, Dialog, DialogContent, IconButton,
  Select, MenuItem, FormControl, InputLabel, Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useConnectForm } from "../api/reactQueryHook/useConnectedForms";
import LoadingOverlay from "./LoadingOverlay";

interface AttachFormDialogProps {
  open: boolean;
  fileName: string;
  uploadedForms: { id: string; name: string }[];
  file_id: number;
  company_id: number;
  library_id: number;
  created_by: number;
  onClose: () => void;
  onAttachSuccess?: () => void;
}

const AttachFormDialog: React.FC<AttachFormDialogProps> = ({
  open, fileName, uploadedForms, file_id, company_id, library_id, created_by, onClose, onAttachSuccess,
}) => {
  const [selectedFormId, setSelectedFormId] = useState<string>("");
  const connectMutation = useConnectForm(company_id);

  const handleAttach = () => {
    const form = uploadedForms.find(f => f.id === selectedFormId);
    if (!form) return;
    connectMutation.mutate(
      { file_id, form_id: Number(selectedFormId), company_id, library_id, created_by, file_name: fileName },
      { onSuccess: () => { setSelectedFormId(""); onClose(); onAttachSuccess?.(); } }
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "10px",
          overflow: "hidden",
        },
      }}
    >
      <LoadingOverlay isLoading={connectMutation.isPending} />
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>

        {/* ── Header ── */}
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 3, pt: 2.5, pb: 2,
          borderBottom: "1px solid", borderColor: "divider",
        }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "text.primary", letterSpacing: "0.04em" }}>
              Attach a form to The BPR file
            </Typography>
            {fileName && (
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.3, fontSize: "0.72rem" }} noWrap>
                {fileName}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose}
            sx={{ color: "text.secondary", "&:hover": { color: "text.primary" } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* ── Dropdown ── */}
        <Box sx={{ px: 3, py: 3 }}>
          {uploadedForms.length === 0 ? (
            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 2 }}>
              No forms uploaded yet. Upload a form first.
            </Typography>
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: "text.secondary" }}>Select a PDF</InputLabel>
              <Select
                value={selectedFormId}
                label="Select a PDF"
                onChange={e => setSelectedFormId(e.target.value)}
                sx={{
                  color: "text.primary",
                  ".MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
                  ".MuiSvgIcon-root": { color: "text.secondary" },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: "background.paper",
                      border: "1px solid",
                      borderColor: "divider",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                    },
                  },
                }}
              >
                {uploadedForms.map(form => (
                  <MenuItem key={form.id} value={form.id}>
                    <Typography variant="body2" sx={{ color: "text.primary" }}>
                      {form.name}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {/* ── Footer ── */}
        {uploadedForms.length > 0 && (
          <Box sx={{ px: 3, pb: 2.5, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ fontWeight: 600, borderRadius: "8px", fontSize: "0.8rem" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!selectedFormId || connectMutation.isPending}
              onClick={handleAttach}
              sx={{ fontWeight: 700, borderRadius: "8px", fontSize: "0.8rem" }}
            >
              {connectMutation.isPending ? "Attaching..." : "Attach"}
            </Button>
          </Box>
        )}

      </DialogContent>
    </Dialog>
  );
};

export default AttachFormDialog;
